import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * PageSkeleton — the loading state shown while a lazily-loaded page's code
 * arrives (Suspense fallback) and, via the exported pieces, while a page's
 * data is still on its way.
 *
 * Why not a spinner: a lone ring in an empty pane reads as "broken" for the
 * 0.3–1.5 s a route chunk takes on the live site, and the page then pops in
 * all at once. A skeleton that mirrors the destination layout keeps the frame
 * stable, so the real content replaces grey blocks in place instead of
 * appearing from nothing.
 *
 * Variant is chosen from the route so each tab gets a plausible frame:
 *   list   — title, filter bar, stack of cards      (Orders, Chat, Text Inbox…)
 *   board  — title, kanban columns                  (Order Board)
 *   grid   — title, stat tiles + a panel            (Dashboard, reports, stock)
 *   table  — title, toolbar, table rows             (history, logs, admin)
 *   panel  — title + one tall content panel         (everything else)
 *
 * Purely presentational: no data, no timers, no network. Announced to screen
 * readers once via role="status"; shimmer is disabled under reduced motion
 * (see .skel in src/index.css).
 */
export type SkeletonVariant = 'list' | 'board' | 'grid' | 'table' | 'panel';

const VARIANT_BY_PREFIX: Array<[RegExp, SkeletonVariant]> = [
  [/^\/(orders|messages|forwarded-inbox|text-inbox|email|customers|handoffs|store-hub|worker|packager)/, 'list'],
  [/^\/(order-board|board)/, 'board'],
  [/^\/(dashboard|reports|store-reports|driver-reports|warehouse-reports|financial|stock-overview|store-stock|warehouse)/, 'grid'],
  [/^\/(history|full-history|warehouse-history|activity|admin|inventory|products|tracker|inventory-tracker|shipping)/, 'table'],
];

export function variantForPath(pathname: string): SkeletonVariant {
  for (const [rx, v] of VARIANT_BY_PREFIX) if (rx.test(pathname)) return v;
  return 'panel';
}

/** One grey block. Width/height via className; shimmer via .skel. */
export const Bone: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div aria-hidden="true" className={`skel rounded-md ${className}`} />
);

/** A card-shaped placeholder matching the order / conversation cards. */
export const CardBone: React.FC<{ lines?: number }> = ({ lines = 2 }) => (
  <div aria-hidden="true" className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex items-center justify-between mb-3">
      <Bone className="h-3 w-28" />
      <Bone className="h-5 w-16 rounded-full" />
    </div>
    <Bone className="h-2.5 w-1/2 mb-2" />
    {Array.from({ length: Math.max(0, lines - 1) }).map((_, i) => (
      <Bone key={i} className={`h-2.5 mb-2 ${i % 2 ? 'w-2/3' : 'w-3/4'}`} />
    ))}
    <div className="flex gap-2 mt-3">
      <Bone className="h-6 w-20 rounded-full" />
      <Bone className="h-6 w-14 rounded-full" />
    </div>
  </div>
);

const TitleBlock: React.FC = () => (
  <div className="flex items-start justify-between gap-4 mb-6">
    <div>
      <Bone className="h-7 w-52 mb-2" />
      <Bone className="h-3 w-72" />
    </div>
    <Bone className="h-10 w-32 rounded-lg" />
  </div>
);

const Toolbar: React.FC = () => (
  <div className="flex flex-wrap items-center gap-2 mb-5">
    <Bone className="h-10 w-64 rounded-lg" />
    <Bone className="h-10 w-28 rounded-lg" />
    <Bone className="h-10 w-28 rounded-lg" />
    <Bone className="h-10 w-20 rounded-lg ml-auto" />
  </div>
);

const ListSkeleton: React.FC = () => (
  <>
    <TitleBlock />
    <Toolbar />
    <div className="space-y-4">
      {[0, 1, 2, 3, 4].map((i) => <CardBone key={i} lines={i % 2 ? 3 : 2} />)}
    </div>
  </>
);

const BoardSkeleton: React.FC = () => (
  <>
    <TitleBlock />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[0, 1, 2, 3].map((c) => (
        <div key={c} className="bg-slate-50 rounded-xl border border-slate-200 p-3 space-y-3">
          <Bone className="h-4 w-24" />
          {[0, 1, 2].slice(0, 3 - (c % 2)).map((r) => <CardBone key={r} lines={1} />)}
        </div>
      ))}
    </div>
  </>
);

const GridSkeleton: React.FC = () => (
  <>
    <TitleBlock />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-slate-200 p-4">
          <Bone className="h-10 w-10 rounded-xl mb-4" />
          <Bone className="h-3 w-24 mb-2" />
          <Bone className="h-7 w-16" />
        </div>
      ))}
    </div>
    <div className="bg-white rounded-2xl border border-slate-200 p-5">
      <Bone className="h-4 w-40 mb-4" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => <Bone key={i} className="h-16 rounded-xl" />)}
      </div>
    </div>
  </>
);

const TableSkeleton: React.FC = () => (
  <>
    <TitleBlock />
    <Toolbar />
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="grid grid-cols-5 gap-4 px-4 py-3 border-b border-slate-100">
        {[0, 1, 2, 3, 4].map((i) => <Bone key={i} className="h-3 w-3/4" />)}
      </div>
      {[0, 1, 2, 3, 4, 5, 6].map((r) => (
        <div key={r} className="grid grid-cols-5 gap-4 px-4 py-3 border-b border-slate-50">
          {[0, 1, 2, 3, 4].map((c) => <Bone key={c} className={`h-3 ${c === 0 ? 'w-full' : c === 4 ? 'w-1/2' : 'w-2/3'}`} />)}
        </div>
      ))}
    </div>
  </>
);

const PanelSkeleton: React.FC = () => (
  <>
    <TitleBlock />
    <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
      <Bone className="h-4 w-1/3" />
      <Bone className="h-3 w-full" />
      <Bone className="h-3 w-5/6" />
      <Bone className="h-3 w-2/3" />
      <Bone className="h-40 w-full rounded-xl mt-2" />
    </div>
  </>
);

export const PageSkeleton: React.FC<{ variant?: SkeletonVariant; label?: string }> = ({ variant, label = 'Loading page' }) => {
  const { pathname } = useLocation();
  const v = variant ?? variantForPath(pathname);
  return (
    <div role="status" aria-live="polite" aria-busy="true" className="p-4 md:p-6" data-skeleton={v}>
      <span className="sr-only">{label}</span>
      {v === 'list' && <ListSkeleton />}
      {v === 'board' && <BoardSkeleton />}
      {v === 'grid' && <GridSkeleton />}
      {v === 'table' && <TableSkeleton />}
      {v === 'panel' && <PanelSkeleton />}
    </div>
  );
};

export default PageSkeleton;
